import React, { useEffect, useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Check, PlusIcon, Search, X } from "lucide-react";
import { ErrorType } from "@/types";
import { useDeleteFacilityMutation } from "@/services/facilityApis";
import { useGetCategoriesQuery } from "@/services/categoryApi";
import { useGetPriceMastersQuery } from "@/services/priceMasterApi";
import { DataTableRowActions } from "./data-table-row-actions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetFacilitiesQuery,
  useUpdateFacilityMutation,
} from "@/services/facilityApis";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import FacilityForm from "../modal/form";
import { Facility } from "@/types/facility";
import TableFilters from "@/components/ui/table/data-table-filter";
import { displayValue, formatToPKR } from "@/lib/helper";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { status } from "@/lib/constants/global";
import { toast } from "@/components/ui/use-toast";
import { FacilityStatus } from "@/types/enums";
interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
  status?: string;
  access_control?: string;
  bookable?: string;
  is_add_on?: string;
  load_add_ons?: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "facility",
};

export default function FacilitiesTableView({ onShowUnavailability }: { onShowUnavailability?: () => void }) {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [updateFacility] = useUpdateFacilityMutation();
  const navigate = useNavigate();

  const { data: categoryData } = useGetCategoriesQuery({
    org_id: orgId,
    query: "type=facility&load_subcategories=true&only_parent=true&sort_key=id&sort_order=desc",
  });

  const hide_bulk_facility = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .hide_bulk_facility ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const categoryMap = React.useMemo(() => {
    const map: Record<number, { name: string; subcategories: Record<number, string> }> = {};
    if (Array.isArray(categoryData?.data)) {
      categoryData.data.forEach((cat) => {
        const subs: Record<number, string> = {};
        if (Array.isArray(cat.subcategories)) {
          cat.subcategories.forEach((sub) => {
            if (sub.id != null) {
              subs[sub.id] = sub.name;
            }
          });
        }
        if (cat.id != null) {
          map[cat.id] = { name: cat.name, subcategories: subs };
        }
      });
    }
    return map;
  }, [categoryData]);

  const { data: priceMasterData } = useGetPriceMastersQuery({
    org_id: orgId,
    query: "item_type=facility_slot",
  });

  const priceMasterMap = React.useMemo(() => {
    const map: Record<number, number> = {};
    if (Array.isArray((priceMasterData as any)?.data)) {
      (priceMasterData as any).data.forEach((rule: any) => {
        map[rule.id] = rule.price;
      });
    }
    return map;
  }, [priceMasterData]);

  const facilities = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).facilities ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData(initialValue);

  const {
    data: facilitiesData,
    isLoading,
    refetch,
  } = useGetFacilitiesQuery(
    {
      query: query + "&load_add_ons=true",
      orgId: orgId,
    },
    {
      skip: query == "",
    }
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [formData, setFormData] = useState<Facility | undefined>(undefined);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const facilitiestableData = React.useMemo(() => {
    return Array.isArray(facilitiesData?.data) ? facilitiesData?.data : [];
  }, [facilitiesData]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [deleteFacility, { isLoading: deleteLoading }] = useDeleteFacilityMutation();

  const handleAddFacility = () => {
    setIsDialogOpen(true);
    setAction("add");
    setFormData(undefined);
  };

  const handleEditFacility = (data: Facility) => {
    setIsDialogOpen(true);
    setAction("edit");
    setFormData(data);
  };

  const handleDeleteFacility = async (data: Facility) => {

    try {
      const resp = await deleteFacility(data.id as number).unwrap();
      if (resp) {
        if (facilitiesData?.filtered_counts == 1 && searchCriteria.offset > 0) {
          setSearchCriteria((prev) => ({
            ...prev,
            offset: prev.offset - 1,
          }));
          setFilterData((prev) => ({ ...prev, offset: prev.offset - 1 }));
        }
        setInputValue("");
        refetch();
        toast({
          variant: "success",
          title: "Facility Deleted Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  }

  const actionsColumn: ColumnDef<Facility> = {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    cell: ({ row }) => (
      <DataTableRowActions
        access={facilities}
        data={row.original}
        refetch={refetch}
        handleEdit={handleEditFacility}
        handleDelete={handleDeleteFacility}
      />
    ),
  };

  const columns: ColumnDef<Facility>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Facility Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="normal-case cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.name}`.length > 20
                          ? `${row.original.name}`.substring(0, 20) + "..."
                          : `${row.original.name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="normal-case text-sm w-fit ">
                    {displayValue(`${row?.original?.name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "min_limit",

      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Min Req. Credits"
          sortKey="min_limit"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{row.original.min_limit}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "category_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Category"
          sortKey="category_name"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const catName =
          row.original.category_name ||
          (row.original.category_id ? categoryMap[row.original.category_id]?.name : undefined);
        return <p>{catName || "—"}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "subcategory_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Sub Category"
          sortKey="subcategory_name"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const subName =
          row.original.subcategory_name ||
          (row.original.category_id && row.original.subcategory_id
            ? categoryMap[row.original.category_id]?.subcategories?.[row.original.subcategory_id]
            : undefined);
        return <p>{subName || "—"}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "parent_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Parent Facility"
          sortKey="parent_name"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p className="normal-case">{row.original.parent_name || "—"}</p>;
      },
      enableSorting: false,
      enableHiding: true,
    },

    {
      accessorKey: "access_control",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Access Control"
          sortKey="access_control"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p>
            {row.original.access_control ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <X className="w-4 h-4 text-red-500" />
            )}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "bookable",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Bookable"
          sortKey="bookable"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p>
            {row.original.bookable ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <X className="w-4 h-4 text-red-500" />
            )}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "price_master_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Price"
          sortKey="price_master_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const { price_master_id } = row.original;
        const price = price_master_id ? priceMasterMap[price_master_id] : undefined;
        return <span className="text-nowrap">{price != null ? formatToPKR(price) : "—"}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "max_capacity",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Capacity"
          sortKey="max_capacity"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{row.original.max_capacity}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <Select
            value={row.original?.status}
            onValueChange={async (value) => {
              try {
                const resp = await updateFacility({
                  ...row.original,
                  status: value as FacilityStatus,
                });
                if (resp) {
                  toast({
                    variant: "success",
                    title: "Status updated successfully",
                  });
                }
              } catch (error) {
                console.error("Error", { error });
                if (error && typeof error === "object" && "data" in error) {
                  const typedError = error as ErrorType;
                  toast({
                    variant: "destructive",
                    title: "Error in Submission",
                    description: `${typedError.data?.detail ?? typedError.data?.message
                      }`,
                  });
                } else {
                  toast({
                    variant: "destructive",
                    title: "Error in Submission",
                    description: `Something Went Wrong.`,
                  });
                }
              }
            }}
            disabled={facilities == "read"}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Status" className="text-gray-400">
                <span className="flex gap-2 items-center">
                  <span
                    className={`${row.original?.status == FacilityStatus.Active
                      ? "bg-green-500"
                      : "bg-blue-500"
                      } rounded-[50%] w-4 h-4`}
                  ></span>
                  <span>
                    {row.original?.status == FacilityStatus.Active
                      ? "Active"
                      : "Inactive"}
                  </span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FacilityStatus.Active}>Active</SelectItem>
              <SelectItem value={FacilityStatus.Inactive}>Inactive</SelectItem>
            </SelectContent>
          </Select>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    ...(facilities !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: facilitiestableData as Facility[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const totalRecords = facilitiesData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (
    field: string,
    value: string | number | number[] | React.ChangeEvent<HTMLInputElement>
  ) => {
    // Update the filter state based on field and value
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: FacilityStatus.Active, name: "Active" },
        { id: FacilityStatus.Inactive, name: "Inactive" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "select",
      name: "access_control",
      label: "Access Control",
      options: [
        { id: "true", name: "Yes" },
        { id: "false", name: "No" },
      ],
      function: (value: string) => handleFilterChange("access_control", value),
    },
    {
      type: "select",
      name: "bookable",
      label: "Bookable",
      options: [
        { id: "true", name: "Yes" },
        { id: "false", name: "No" },
      ],
      function: (value: string) => handleFilterChange("bookable", value),
    },
    {
      type: "select",
      name: "is_add_on",
      label: "Is Add On",
      options: [
        { id: "true", name: "Yes" },
        { id: "false", name: "No" },
      ],
      function: (value: string) => handleFilterChange("is_add_on", value),
    },
    {
      type: "select",
      name: "is_parent",
      label: "Facility Hierarchy",
      options: [
        { id: "true", name: "Parent Facilities" },
        { id: "false", name: "Child Facilities" },
      ],
      function: (value: string) => handleFilterChange("is_parent", value),
    },
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        {/* Table Filter component */}
        <div className="flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={setSearchCriteria}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {hide_bulk_facility == "full_access" && (
            <Button
              access={facilities !== "read"}
              className="bg-primary text-sm text-black flex items-center gap-1 lg:mb-0 h-8 px-2"
              onClick={onShowUnavailability}
            >
              <i className="fa fa-calendar-xmark h-4 w-4"></i>
              Bulk Facility
            </Button>
          )}

          {/* Single Create New Button - Responsive */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  access={facilities !== "read"}
                  className="bg-primary text-black flex items-center justify-center h-9 w-9 md:h-8 md:w-auto md:px-3 md:gap-1 p-0 rounded-md"
                  onClick={handleAddFacility}
                >
                  <PlusIcon className="h-4 w-4 shrink-0" />
                  <span className="hidden md:inline text-sm">Create New</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="md:hidden">
                <p>Create New</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Filter Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`border rounded-md h-9 w-9 md:h-8 md:w-8 text-gray-400 flex items-center justify-center shrink-0 ${
                    Object.keys(filterData).length ? "border-primary" : ""
                  }`}
                  onClick={() => setOpenFilter(true)}
                >
                  <i
                    className={`fa fa-filter ${
                      Object.keys(filterData).length ? "text-primary" : ""
                    }`}
                  ></i>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>click to apply filter</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className="hidden md:block rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (
                        facilities !== "read" &&
                        e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') &&
                        (e.target === e.currentTarget || e.target.closest("td"))
                      ) {
                        handleEditFacility(row.original as unknown as Facility);
                      }
                    }}
                    className={
                      facilities !== "read"
                        ? "cursor-pointer hover:bg-secondary"
                        : ""
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : facilitiestableData.length > 0 ? (
                <TableRow className="!p-0">
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center  "
                  >
                    No facility found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center py-10">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : facilitiestableData.length ? (
          facilitiestableData.map((facility: Facility) => {
            const catName =
              facility.category_name ||
              (facility.category_id ? categoryMap[facility.category_id]?.name : undefined);
            const subName =
              facility.subcategory_name ||
              (facility.category_id && facility.subcategory_id
                ? categoryMap[facility.category_id]?.subcategories?.[facility.subcategory_id]
                : undefined);
            const price = facility.price_master_id
              ? priceMasterMap[facility.price_master_id]
              : undefined;
            return (
              <div
                key={facility.id}
                className="border border-border rounded-md p-3 space-y-2 bg-white"
                onClick={() => facilities !== "read" && handleEditFacility(facility)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm normal-case truncate">
                      {displayValue(`${facility.name}`)}
                    </p>
                    <p className="text-xs text-gray-500 normal-case truncate">
                      {catName || "—"}
                      {subName ? ` / ${subName}` : ""}
                    </p>
                  </div>
                  <div
                    className="flex items-center gap-2 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      className={`${facility.status == FacilityStatus.Active
                          ? "bg-green-500"
                          : "bg-blue-500"
                        } rounded-full w-3 h-3 shrink-0`}
                    ></span>
                    {facilities !== "read" && (
                      <DataTableRowActions
                        access={facilities}
                        data={facility}
                        refetch={refetch}
                        handleEdit={handleEditFacility}
                        handleDelete={handleDeleteFacility}
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <div className="text-gray-500">Min Req. Credits</div>
                  <div className="text-right">{facility.min_limit}</div>

                  <div className="text-gray-500">Capacity</div>
                  <div className="text-right">{facility.max_capacity}</div>

                  <div className="text-gray-500">Price</div>
                  <div className="text-right">
                    {price != null ? formatToPKR(price) : "—"}
                  </div>

                  <div className="text-gray-500">Bookable</div>
                  <div className="flex justify-end">
                    {facility.bookable ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                  </div>

                  <div className="text-gray-500">Access Control</div>
                  <div className="flex justify-end">
                    {facility.access_control ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            No records found.
          </div>
        )}
      </div>

      {/* pagination */}
      {facilitiestableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <FacilityForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        data={formData}
        action={action}
        setAction={setAction}
        setData={setFormData}
        refetch={refetch}
      />
    </div>
  );
}
